/**
 * API client: Bearer token, one shared refresh on 401, typed errors.
 */
import { readTokens, writeTokens } from '@/auth/tokens'
import type {
  ApiErrorBody,
  ApiErrorCode,
  ClientKind,
  LinkResult,
  Coverage,
  DialogDetail,
  DialogSummary,
  DialogTurn,
  ImageSearchResult,
  SecondLook,
  Payment,
  PhotoPlace,
  PaymentStatus,
  Profile,
  SearchResult,
  StarsInvoice,
  Tariff,
  TelegramLoginConfig,
  Tokens,
} from '@/api/types'

export class ApiError extends Error {
  code: ApiErrorCode
  status: number
  details?: Record<string, unknown>

  constructor(status: number, code: ApiErrorCode, message: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  get isPaywall(): boolean {
    return this.code === 'daily_limit_reached' || this.code === 'follow_up_limit_reached'
  }
}

type Listener = () => void

class ApiClient {
  private client: ClientKind = 'pwa'
  private refreshing: Promise<Tokens | null> | null = null
  private onLogout: Listener | null = null

  setClient(kind: ClientKind): void {
    this.client = kind
  }

  setLogoutHandler(handler: Listener | null): void {
    this.onLogout = handler
  }

  get tokens(): Tokens | null {
    return readTokens()
  }

  setTokens(tokens: Tokens | null): void {
    writeTokens(tokens)
  }

  async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const headers = new Headers(init.headers)
    headers.set('X-Client', this.client)
    if (!(init.body instanceof FormData) && init.body !== undefined) {
      headers.set('Content-Type', 'application/json')
    }

    const tokens = this.tokens
    if (tokens) {
      headers.set('Authorization', `Bearer ${tokens.access_token}`)
    }

    const response = await fetch(path, { ...init, headers })

    if (response.status === 401 && retry && tokens?.refresh_token) {
      const refreshed = await this.refresh()
      if (refreshed) {
        return this.request<T>(path, init, false)
      }
    }

    if (response.status === 204) {
      return undefined as T
    }

    if (!response.ok) {
      throw await this.toError(response)
    }

    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as T
  }

  /** Parallel 401s share one refresh, so the token is not rotated twice. */
  private refresh(): Promise<Tokens | null> {
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = null
      })
    }
    return this.refreshing
  }

  private async doRefresh(): Promise<Tokens | null> {
    const tokens = this.tokens
    if (!tokens?.refresh_token) return null

    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client': this.client },
        body: JSON.stringify({ refresh_token: tokens.refresh_token }),
      })
      if (!response.ok) {
        this.setTokens(null)
        this.onLogout?.()
        return null
      }
      const fresh = (await response.json()) as Tokens
      this.setTokens(fresh)
      return fresh
    } catch {
      return null
    }
  }

  private async toError(response: Response): Promise<ApiError> {
    let code: ApiErrorCode = 'internal_error'
    let message = response.statusText
    let details: Record<string, unknown> | undefined

    try {
      const body = (await response.json()) as ApiErrorBody
      if (body?.error) {
        code = body.error.code
        message = body.error.message
        details = body.error.details
      }
    } catch {
      // A response that is not our envelope, e.g. from nginx
    }

    if (response.status === 401) {
      this.setTokens(null)
      this.onLogout?.()
    }

    return new ApiError(response.status, code, message, details)
  }

  // --- auth ---

  requestEmailCode(email: string): Promise<void> {
    return this.request('/api/auth/email/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  verifyEmailCode(email: string, code: string): Promise<Tokens> {
    return this.request<Tokens>('/api/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    })
  }

  loginWithTelegram(initData: string): Promise<Tokens> {
    return this.request<Tokens>('/api/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ init_data: initData }),
    })
  }

  telegramLoginConfig(): Promise<TelegramLoginConfig> {
    return this.request<TelegramLoginConfig>('/api/auth/telegram/oidc')
  }

  loginWithTelegramCode(code: string, codeVerifier: string): Promise<Tokens> {
    return this.request<Tokens>('/api/auth/telegram/oidc', {
      method: 'POST',
      body: JSON.stringify({ code, code_verifier: codeVerifier }),
    })
  }

  logout(): Promise<void> {
    const tokens = this.tokens
    if (!tokens) return Promise.resolve()
    return this.request('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: tokens.refresh_token }),
    })
  }

  // --- profile ---

  me(): Promise<Profile> {
    return this.request<Profile>('/api/me')
  }

  requestEmailAttach(email: string): Promise<void> {
    return this.request('/api/me/email/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  linkTelegram(code: string, codeVerifier: string): Promise<LinkResult> {
    return this.request<LinkResult>('/api/me/telegram', {
      method: 'POST',
      body: JSON.stringify({ code, code_verifier: codeVerifier }),
    })
  }

  mergeAccounts(ticket: string): Promise<Tokens> {
    return this.request<Tokens>('/api/me/merge', {
      method: 'POST',
      body: JSON.stringify({ ticket }),
    })
  }

  verifyEmailAttach(email: string, code: string): Promise<LinkResult> {
    return this.request<LinkResult>('/api/me/email/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    })
  }

  // --- search ---

  /** The server widens the radius itself when nothing is close; radius_m says how far it went. */
  nearby(lat: number, lon: number, radius = 200): Promise<SearchResult> {
    const query = new URLSearchParams({ lat: String(lat), lon: String(lon), radius: String(radius), widen: 'true' })
    return this.request<SearchResult>(`/api/attractions/nearby?${query}`)
  }

  /** log=false while the user is still typing: the search is not counted and activity_id comes back null. */
  searchByName(query: string, log = true): Promise<SearchResult> {
    const params = new URLSearchParams({ q: query })
    if (!log) params.set('log', 'false')
    return this.request<SearchResult>(`/api/attractions/search?${params}`)
  }

  searchByImage(file: Blob, place?: PhotoPlace | null): Promise<ImageSearchResult> {
    const form = new FormData()
    form.append('image', file, 'photo.jpg')
    if (place) {
      form.append('lat', String(place.lat))
      form.append('lon', String(place.lon))
      form.append('place_source', place.source)
    }
    return this.request<ImageSearchResult>('/api/attractions/by-image', { method: 'POST', body: form })
  }

  /** Free: the vision model names the place the search was unsure about. */
  secondLook(file: Blob, activityId: number | null, shownIds: number[]): Promise<SecondLook> {
    const form = new FormData()
    form.append('image', file, 'photo.jpg')
    if (activityId !== null) form.append('activity_id', String(activityId))
    form.append('shown_ids', shownIds.join(','))
    return this.request<SecondLook>('/api/attractions/by-image/second-look', { method: 'POST', body: form })
  }

  /** A dialog about what is in the photo; spends the daily limit. */
  describePhoto(file: Blob, activityId: number | null, name: string | null): Promise<DialogTurn> {
    const form = new FormData()
    form.append('image', file, 'photo.jpg')
    if (activityId !== null) form.append('activity_id', String(activityId))
    if (name) form.append('name', name)
    return this.request<DialogTurn>('/api/attractions/by-image/describe', { method: 'POST', body: form })
  }

  // --- coverage ---

  coverage(): Promise<Coverage> {
    return this.request<Coverage>('/api/coverage')
  }

  // --- dialogs ---

  startDialog(attractionId: number, source: string, activityId?: number | null): Promise<DialogTurn> {
    return this.request<DialogTurn>('/api/dialogs', {
      method: 'POST',
      body: JSON.stringify({ attraction_id: attractionId, source, activity_id: activityId ?? null }),
    })
  }

  ask(dialogId: number, question: string): Promise<DialogTurn> {
    return this.request<DialogTurn>(`/api/dialogs/${dialogId}/ask`, {
      method: 'POST',
      body: JSON.stringify({ question }),
    })
  }

  askFollowUp(dialogId: number, index: number): Promise<DialogTurn> {
    return this.request<DialogTurn>(`/api/dialogs/${dialogId}/ask`, {
      method: 'POST',
      body: JSON.stringify({ follow_up_index: index }),
    })
  }

  dialogs(): Promise<DialogSummary[]> {
    return this.request<DialogSummary[]>('/api/dialogs')
  }

  dialog(dialogId: number): Promise<DialogDetail> {
    return this.request<DialogDetail>(`/api/dialogs/${dialogId}`)
  }

  // --- billing ---

  tariffs(): Promise<Tariff[]> {
    return this.request<Tariff[]>('/api/billing/tariffs')
  }

  createPayment(tariffDays: number, email?: string, phone?: string): Promise<Payment> {
    return this.request<Payment>('/api/billing/payments', {
      method: 'POST',
      body: JSON.stringify({ tariff_days: tariffDays, email: email ?? null, phone: phone ?? null }),
    })
  }

  createStarsInvoice(tariffDays: number, test = false): Promise<StarsInvoice> {
    return this.request<StarsInvoice>('/api/billing/stars-invoices', {
      method: 'POST',
      body: JSON.stringify({ tariff_days: tariffDays, test }),
    })
  }

  paymentStatus(orderId: string): Promise<PaymentStatus> {
    return this.request<PaymentStatus>(`/api/billing/payments/${orderId}`)
  }
}

export const api = new ApiClient()
export type { ApiClient }
